import React from "react";
import { Eye } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Btn } from "@/components/common";

/**
 * THE SECOND DOOR INTO A RECORD — one that can only be read. (ZEN-702)
 *
 * WHY THIS EXISTS. On nine screens the pencil was the only way into the
 * details of a record. For the account owner that meant opening the form
 * that can CHANGE a tax record every time she only wanted to read one, and
 * backing out again. For a beneficiary, who cannot edit expenses, it
 * meant no way in at all: one table row, and nothing behind it — not the
 * exchange rate, not the school term, not who paid, not the notes — on the one
 * screen their login exists for.
 *
 * ONE PANEL, NOT NINE. "Show me this record" has one answer in this app. Every
 * list hands this component a record it ALREADY HOLDS and a list of labelled
 * values (built in lib/recordFields.js, where every label lives in one place),
 * and this draws them. Nothing else:
 *
 *   · it holds no form state and has no save — there is nothing here a bug
 *     could re-enable into a write;
 *   · it FETCHES NOTHING — so it cannot show a field the account was not
 *     already given. If a field is not on the page, it is not on the panel.
 *     That is what makes this a screens-only change with no permission
 *     question in it;
 *   · a field with no value is not drawn at all. "Notes —" is noise, and on a
 *     panel of twenty fields it becomes most of the panel.
 *
 * WHY NOT THE EDITING FORM IN A DISABLED STATE. Considered and rejected on the
 * ticket: the expense form is ~2,500 lines and fetches as it opens — rates,
 * payers, accounts — requests a beneficiary's account is not entitled to make,
 * and every disabled control is still a control.
 *
 * NO WAY THROUGH TO A FORM, FOR ANYONE. (Maryse, 2026-09-26.) The panel used
 * to offer an Edit button at the bottom, as the ticket originally asked. She
 * removed it on sight, and the reason is worth keeping: nobody edits from this
 * panel, and every form window in this app ends with a plain button on the
 * left and a blue Save on the right. A blue button in that exact place that
 * navigates instead of writing teaches the hand the wrong reflex on screens
 * full of tax records. There is nothing here that could be saved either — no
 * boxes, no form state — so a Save button would have no job.
 *
 * Closing costs one click and the pencil is still beside the eye that was just
 * pressed. The panel now has exactly one control, and four ways out that all
 * do the same harmless thing: Close, the X, the Escape key, and the dark area
 * around it.
 */

/** The one rule for "this field has nothing to show". */
export function hasValue(v) {
  if (v === null || v === undefined) return false;
  if (typeof v === "string") return v.trim() !== "";
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === "number") return !Number.isNaN(v);
  return true;
}

/**
 * Drops the empty fields, and any heading that would then introduce nothing.
 * Exported so the rule can be tested without a DOM.
 */
export function visibleFields(fields) {
  const out = [];
  let pendingHeading = null;
  for (const f of fields || []) {
    if (!f) continue;
    if (f.heading !== undefined) {
      // A heading with its own summary line has something to show by itself.
      if (hasValue(f.detail)) { out.push(f); pendingHeading = null; } else pendingHeading = f;
      continue;
    }
    if (f.table !== undefined) {
      if (!f.table?.rows?.length) continue;
    } else if (!hasValue(f.value)) continue;
    if (pendingHeading) { out.push(pendingHeading); pendingHeading = null; }
    out.push(f);
  }
  return out;
}

/**
 * The eye. Grey on purpose — reading is the quieter of the two actions and
 * must not compete with the blue pencil beside it, which changes something.
 * Sits IMMEDIATELY BEFORE the pencil on every list (Maryse's placement,
 * 2026-09-25, not open to reinterpretation), so the two doors into a record
 * are always adjacent and always in the same order.
 */
export const VIEW_LABEL = "View details";

// variant: "icon" (padded, the shape of the pencil beside it on most lists),
// "bare" (no padding, for the lists whose pencil is bare — Accounts), or a
// text link when children are given (the distribution card's row of links).
export function ViewButton({ onClick, testid, size = 16, className = "", children, label = VIEW_LABEL, variant = "icon", ...rest }) {
  const base = children
    ? "inline-flex items-center gap-1 text-[11px] font-semibold text-[#6b7280] hover:underline"
    : variant === "bare"
      ? "text-[#6b7280] hover:text-[#1f2937]"
      : "rounded p-1.5 text-[#6b7280] hover:bg-[#E9E9EB]";
  return (
    <button type="button" onClick={onClick} data-testid={testid} data-record-view-button=""
      aria-label={label} title={label} className={`${base} ${className}`} {...rest}>
      <Eye size={size} weight="bold" />
      {children}
    </button>
  );
}

/**
 * The read-only panel.
 *
 * props
 *   open, onClose   — the dialog's state, held by the caller
 *   title           — "Expense details" and so on (lib/recordFields.js RECORD_TITLES)
 *   subtitle        — the record's own label, so the row's facts stay in view
 *   fields          — [{label, value}] with optional {heading, detail?}
 *                     separators and {table: {title, columns, rows}} blocks
 * There is deliberately no way to reach the editing form from here — see the
 * note at the top of this file.
 */
/**
 * A small table under its own heading — one academic year of school terms
 * (ZEN-783). A cell is a string, or {text, span} to run across columns; a row's
 * `detail` is a quieter line under it. Columns that hold amounts sit right.
 */
function FieldTable({ table, testid }) {
  const cols = table.columns || [];
  const cellText = (c) => (c && typeof c === "object" ? c.text : c);
  return (
    <div className="mt-1 sm:col-span-2" data-testid={`${testid}-table`}>
      <div className="text-sm font-semibold text-[#1f2937]" data-testid={`${testid}-table-title`}>{table.title}</div>
      <table className="mt-1 w-full border-collapse text-xs">
        <thead>
          <tr className="border-b border-[#E9E9EB] text-left text-[10px] font-bold uppercase tracking-wide text-[#6b7280]">
            {cols.map((c, i) => <th key={i} scope="col" className={`py-1 pr-2 align-bottom font-bold ${i >= 2 ? "text-right" : ""}`}>{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((r, ri) => (
            <React.Fragment key={ri}>
              <tr className={r.detail ? "" : "border-b border-[#F1F2F4]"} data-testid={`${testid}-table-row`}>
                {r.cells.map((c, ci) => {
                  const start = r.cells.slice(0, ci).reduce((n, x) => n + ((x && typeof x === "object" && x.span) || 1), 0);
                  return (
                    <td key={ci} colSpan={(c && typeof c === "object" && c.span) || 1}
                      className={`py-1 pr-2 align-top text-[#1f2937] tnum ${start >= 2 ? `text-right ${c && typeof c === "object" ? "" : "whitespace-nowrap"}` : "break-words"}`}>{cellText(c) || "\u2014"}</td>
                  );
                })}
              </tr>
              {r.detail && (
                <tr className="border-b border-[#F1F2F4]"><td colSpan={cols.length} className="break-words pb-1 pr-2 text-[11px] text-[#6b7280]" data-testid={`${testid}-table-detail`}>{r.detail}</td></tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function RecordView({ open, onClose, title, subtitle, fields, testid = "record-view" }) {
  const rows = visibleFields(fields);
  return (
    <Dialog open={!!open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto" data-testid={testid} data-record-view="">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {subtitle
            ? <DialogDescription data-testid={`${testid}-subtitle`}>{subtitle}</DialogDescription>
            : <DialogDescription className="sr-only">{title}</DialogDescription>}
        </DialogHeader>
        <dl className="grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-[minmax(0,11rem)_1fr]" data-testid={`${testid}-fields`}>
          {rows.map((f, i) => f.heading !== undefined ? (
            <div key={`h-${i}`} className="mt-2 border-t border-[#E9E9EB] pt-2 sm:col-span-2">
              <div className="text-xs font-bold uppercase tracking-wide text-[#034AA2]" data-testid={`${testid}-heading`}>{f.heading}</div>
              {hasValue(f.detail) && <div className="mt-0.5 text-sm text-[#1f2937]" data-testid={`${testid}-heading-detail`}>{f.detail}</div>}
            </div>
          ) : f.table !== undefined ? (
            <FieldTable key={`t-${i}`} table={f.table} testid={testid} />
          ) : (
            <React.Fragment key={`f-${i}`}>
              <dt className="text-xs font-bold uppercase tracking-wide text-[#6b7280] sm:pt-0.5" data-testid={`${testid}-label`}>{f.label}</dt>
              <dd className="min-w-0 whitespace-pre-wrap break-words text-[#1f2937] tnum" data-testid={`${testid}-value`} data-label={f.label}>{f.value}</dd>
            </React.Fragment>
          ))}
        </dl>
        {/* ONE control, and it only closes. Nothing in this footer writes, and
            nothing here leads anywhere. */}
        <DialogFooter>
          <Btn variant="ghost" onClick={onClose} data-testid={`${testid}-close`}>Close</Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
